import React, { FunctionComponent, useMemo, useState, useEffect } from 'react';

import _ from 'lodash';
import { DateTime } from 'luxon';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { MetaData } from 'common/response-editor/MetadataParser';
import { ResponseEditorRenderer } from 'common/response-editor/renderer/ResponseEditorRenderer';
import { Collage, CollageGallery } from 'social/common/collage/Collage';
import { getFirstLinkFromMetaData } from 'social/common/util/Util';
import { ErrorDisplay } from 'social/publisher/error-display/ErrorDisplay';
import {
    LocationSource,
    LocationUid,
    SelectedSource,
    SourceName,
    UrlMetaData,
    GoogleButtonOption,
    GoogleFields,
    PublisherError,
    LocationTag,
    FacebookTargetFields,
    MediaPathsToAltText,
} from 'social/publisher/Publisher.types';
import { GOOGLE_BUTTON_OPTIONS } from 'social/publisher/util/Constants';

import 'social/publisher/source-preview/SourcePreview.scss';

interface Props {
    locationSources: LocationSource[];
    selectedLocations: LocationUid[];
    selectedSources: SelectedSource;
    media: any[];
    content: MetaData[];
    urlMetaData: UrlMetaData | null;
    googleFields: GoogleFields;
    publisherErrors: PublisherError[];
    title: string | undefined;
    locationTag: LocationTag | null;
    targetAudience: { FACEBOOK: FacebookTargetFields } | null;
    isDMLinkAdded: boolean;
    customizeSourceKey: SourceName | 'ALL_SOURCES';
    mediaPathsToAltText?: MediaPathsToAltText;
}

export const SourcePreview: FunctionComponent<Props> = ({
    locationSources,
    selectedLocations,
    selectedSources,
    media,
    content,
    urlMetaData,
    googleFields,
    publisherErrors,
    title,
    locationTag,
    targetAudience,
    isDMLinkAdded,
    customizeSourceKey,
    mediaPathsToAltText,
}) => {
    const { t } = useTranslation(['social/publisher']);
    const defaultLocationName = t('DEFAULT_LOCATION_NAME');
    const [locationNames, setLocationNames] = useState({
        facebook: defaultLocationName,
        instagram: defaultLocationName,
        twitter: defaultLocationName,
        googlePlaces: defaultLocationName,
        linkedin: defaultLocationName,
        youtube: defaultLocationName,
        igStories: defaultLocationName,
        igReels: defaultLocationName,
        tiktok: defaultLocationName,
    });
    const [numSelectedSources, setNumSelectedSources] = useState(0);

    const selectedLocationObjects = useMemo(() => {
        return locationSources.filter((loc) => {
            return _.some(selectedLocations, (selectedLuid) => {
                return loc.lUid === selectedLuid;
            });
        });
    }, [locationSources, selectedLocations]);

    useEffect(() => {
        const getFirstMatchedLocation = (source: SourceName): LocationSource | undefined => {
            return _.find(selectedLocationObjects, (loc) => {
                return (
                    loc.sources[source] === 0 ||
                    loc.sources[source] === 1 ||
                    loc.sources[source] === 'VALID' ||
                    loc.sources[source] === 'INVALID'
                );
            });
        };

        const facebookLocation = getFirstMatchedLocation('FACEBOOK');
        const instagramLocation = getFirstMatchedLocation('INSTAGRAM');
        const twitterLocation = getFirstMatchedLocation('TWITTER');
        const googlePlacesLocation = getFirstMatchedLocation('GOOGLE_PLACES');
        const linkedinLocation = getFirstMatchedLocation('LINKEDIN');
        const youtubeLocation = getFirstMatchedLocation('YOUTUBE');
        const igStoriesLocation = getFirstMatchedLocation('INSTAGRAM');
        const igReelsLocation = getFirstMatchedLocation('INSTAGRAM');
        const tiktokLocation = getFirstMatchedLocation('TIKTOK');

        setLocationNames({
            facebook: facebookLocation?.lName || defaultLocationName,
            instagram: instagramLocation?.lName || defaultLocationName,
            twitter: twitterLocation?.lName || defaultLocationName,
            googlePlaces: googlePlacesLocation?.lName || defaultLocationName,
            linkedin: linkedinLocation?.lName || defaultLocationName,
            youtube: youtubeLocation?.lName || defaultLocationName,
            igStories: igStoriesLocation?.lName || defaultLocationName,
            igReels: igReelsLocation?.lName || defaultLocationName,
            tiktok: tiktokLocation?.lName || defaultLocationName,
        });
    }, [locationSources, selectedLocations, selectedSources, defaultLocationName, selectedLocationObjects]);

    useEffect(() => {
        let numSelectedSources = 0;

        Object.values(selectedSources).forEach((value) => {
            if (value) {
                numSelectedSources += 1;
            }
        });

        setNumSelectedSources(numSelectedSources);
    }, [selectedSources]);

    let linkPreview;

    const getRenderedContentBySource = (source: SourceName): JSX.Element => {
        const previewContent = content.filter((el) => {
            const contentSource: string = _.get(el, 'attributes.additionalInfo.source');

            return !contentSource || contentSource === source;
        });

        return <ResponseEditorRenderer content={previewContent} />;
    };

    const collageData: CollageGallery[] = media.map((med, i) => {
        const { url, type } = med;

        return {
            type,
            url,
        };
    });
    const buildCollage = (collageData: CollageGallery[], sourceMax = 1, maxCount = 1): JSX.Element => {
        if (collageData.length > 0) {
            return (
                <Collage
                    galleryData={collageData.slice(0, sourceMax)}
                    maxCount={maxCount}
                    mediaPathsToAltText={mediaPathsToAltText}
                />
            );
        }

        return <></>;
    };

    if (urlMetaData) {
        // display the link user entered, not the url in urlMetaData since they could be slightly different (www.nytimes.com VS https://www.nytimes.com)
        const link = getFirstLinkFromMetaData(content);

        linkPreview = (
            <div className="preview-link">
                {!_.isEmpty(urlMetaData.images) && (
                    <div className="link-image">
                        <Collage
                            galleryData={[{ url: urlMetaData.images[0].url, type: 'image' }]}
                            maxCount={1}
                            openModalOnMediaClick={false}
                        />
                    </div>
                )}
                <div className="link-info">
                    <div className="link-title">{urlMetaData.title}</div>
                    <div className="link-description">{urlMetaData.description}</div>
                    <div className="link-url">{link}</div>
                </div>
            </div>
        );
    }

    const facebook = (
        <>
            <div className="source-preview-facebook source-preview-container">
                <div className="preview-header">
                    <div className="social-icon facebook" />
                    <div className="preview-header-location">{locationNames.facebook}</div>
                    <div className="preview-header-time">
                        {targetAudience?.FACEBOOK?.isUnpublished ? t('SPONSORED') : t('JUST_NOW')}
                    </div>
                </div>
                <div className="preview-content">
                    <ErrorDisplay publisherErrors={publisherErrors} component="CONTENT" source="FACEBOOK" />
                    <div className="preview-content-display">{getRenderedContentBySource('FACEBOOK')}</div>
                    <ErrorDisplay publisherErrors={publisherErrors} component="MEDIA" source="FACEBOOK" />
                    <div className="preview-content-media">{buildCollage(collageData, 10, 5)}</div>
                    {linkPreview}
                </div>
                <div className="preview-footer">
                    <div className="preview-footer-iconset">
                        <i className="i icon-thumbs-up" />
                        <span>{t('LIKE')}</span>
                    </div>
                    <div className="preview-footer-iconset">
                        <i className="i icon-comment-alt-dots" />
                        <span>{t('COMMENT')}</span>
                    </div>
                    <div className="preview-footer-iconset">
                        <i className="i icon-share-alt" />
                        <span>{t('SHARE')}</span>
                    </div>
                </div>
            </div>
        </>
    );

    const instagram = (
        <>
            <div className="source-preview-instagram source-preview-container">
                <div className="preview-header">
                    <div className="social-icon instagram" />
                    <div className="preview-header-location">{locationNames.instagram}</div>
                </div>
                <div className="preview-content">
                    <ErrorDisplay publisherErrors={publisherErrors} component="MEDIA" source="INSTAGRAM" />
                    <div className="preview-content-media">{buildCollage(collageData, 10, 5)}</div>
                    <ErrorDisplay publisherErrors={publisherErrors} component="CONTENT" source="INSTAGRAM" />
                    <div className="preview-content-display">{getRenderedContentBySource('INSTAGRAM')}</div>
                </div>
                <div className="preview-footer">
                    <div className="preview-footer-iconset">
                        <i className="i icon-heart" />
                        <span>{t('LIKE')}</span>
                    </div>
                    <div className="preview-footer-iconset">
                        <i className="i icon-comment-alt-dots" />
                        <span>{t('COMMENT')}</span>
                    </div>
                </div>
            </div>
        </>
    );

    const igReels = (
        <>
            <div className="source-preview-instagram source-preview-container">
                <div className="preview-header">
                    <div className="social-icon ig_reels" />
                    <div className="preview-header-location">{locationNames.igReels}</div>
                </div>
                <div className="preview-content">
                    <ErrorDisplay publisherErrors={publisherErrors} component="MEDIA" source="IG_REELS" />
                    <div className="preview-content-media">{buildCollage(collageData, 10, 5)}</div>
                    <ErrorDisplay publisherErrors={publisherErrors} component="CONTENT" source="IG_REELS" />
                    <div className="preview-content-display">{getRenderedContentBySource('IG_REELS')}</div>
                </div>
                <div className="preview-footer">
                    <div className="preview-footer-iconset">
                        <i className="i icon-heart" />
                        <span>{t('LIKE')}</span>
                    </div>
                    <div className="preview-footer-iconset">
                        <i className="i icon-comment-alt-dots" />
                        <span>{t('COMMENT')}</span>
                    </div>
                </div>
            </div>
        </>
    );

    const twitter = (
        <>
            <div className="source-preview-twitter source-preview-container">
                <div className="preview-header">
                    <div className="social-icon twitter" />
                    <div className="preview-header-location">{locationNames.twitter}</div>
                    <div className="preview-header-time">{t('JUST_NOW')}</div>
                </div>
                <div className="preview-content">
                    <ErrorDisplay publisherErrors={publisherErrors} component="CONTENT" source="TWITTER" />
                    <div className="preview-content-display">{getRenderedContentBySource('TWITTER')}</div>
                    <ErrorDisplay publisherErrors={publisherErrors} component="MEDIA" source="TWITTER" />
                    <div className="preview-content-media">{buildCollage(collageData, 4, 4)}</div>
                </div>
                {linkPreview}
                {locationTag && (
                    <div className="preview-location-tag">
                        <i className="icon-map-marker-alt" />
                        <span>{locationTag.fullName}</span>
                    </div>
                )}

                {isDMLinkAdded && (
                    <div className="border-blue4 rounded padding5-10 blue4 inline-block bottom20">
                        <i className="icon-envelope right5" />
                        <span className="bold">{t('SEND_US_PRIVATE_MESSAGE')}</span>
                    </div>
                )}

                <div className="preview-footer">
                    <div className="preview-footer-iconset">
                        <i className="i icon-comment-alt-dots" />
                        <span>{t('COMMENT')}</span>
                    </div>
                    <div className="preview-footer-iconset">
                        <i className="i icon-retweet" />
                        <span>{t('RETWEET')}</span>
                    </div>
                    <div className="preview-footer-iconset">
                        <i className="i icon-heart" />
                        <span>{t('LIKE')}</span>
                    </div>
                </div>
            </div>
        </>
    );

    // Google Business Profile
    let googleEvent;
    let googleButton;

    if (googleFields.event) {
        let dateRangeDisplay;
        const getDateDisplay = (milliseconds: number): string =>
            DateTime.fromMillis(milliseconds).toLocaleString(DateTime.DATE_FULL);

        if (googleFields.event.startDate && googleFields.event.endDate) {
            dateRangeDisplay = `${getDateDisplay(googleFields.event.startDate)} - ${getDateDisplay(
                googleFields.event.endDate,
            )}`;
        }
        googleEvent = (
            <div className="preview-content-event">
                <div className="preview-content-event-title">{googleFields.event.title}</div>
                <div className="preview-content-event-date-range">{dateRangeDisplay}</div>
            </div>
        );
    }
    if (googleFields.callToAction?.buttonType) {
        const buttonType = googleFields.callToAction?.buttonType;
        const button = GOOGLE_BUTTON_OPTIONS.find((option) => option.value === buttonType) as GoogleButtonOption;

        googleButton = (
            <div className="preview-content-button">
                <Button variant="link">{t(button.label)}</Button>
            </div>
        );
    }

    const gmb = (
        <>
            <div className="source-preview-precontent">
                <div className="social-icon google-mybusiness" />
                <span className="source-preview-title">{t('GOOGLE_MY_BUSINESS_PREVIEW')}</span>
            </div>
            <div className="source-preview-gmb source-preview-container">
                <div className="preview-header">
                    <div className="preview-header-time">{t('POSTED_JUST_NOW')}</div>
                </div>
                <div className="preview-content">
                    {googleEvent}
                    <ErrorDisplay publisherErrors={publisherErrors} component="CONTENT" source="GOOGLE_PLACES" />
                    <div className="preview-content-display">{getRenderedContentBySource('GOOGLE_PLACES')}</div>
                    <ErrorDisplay publisherErrors={publisherErrors} component="MEDIA" source="GOOGLE_PLACES" />
                    <div className="preview-content-media">{buildCollage(collageData, 1, 1)}</div>
                    {googleButton}
                </div>
            </div>
        </>
    );

    const linkedin = (
        <>
            <div className="source-preview-linkedin source-preview-container">
                <div className="preview-header">
                    <div className="social-icon linkedin" />
                    <div className="preview-header-location">{locationNames.linkedin}</div>
                    <div className="preview-header-time">{t('JUST_NOW')}</div>
                </div>
                <div className="preview-content">
                    <ErrorDisplay publisherErrors={publisherErrors} component="CONTENT" source="LINKEDIN" />
                    <div className="preview-content-display">{getRenderedContentBySource('LINKEDIN')}</div>
                    <ErrorDisplay publisherErrors={publisherErrors} component="MEDIA" source="LINKEDIN" />
                    <div className="preview-content-media">{buildCollage(collageData, 1, 1)}</div>
                </div>
                {linkPreview}
                <div className="preview-footer">
                    <div className="preview-footer-iconset">
                        <i className="i icon-thumbs-up" />
                        <span>{t('LIKE')}</span>
                    </div>
                    <div className="preview-footer-iconset">
                        <i className="i icon-comment-alt-dots" />
                        <span>{t('COMMENT')}</span>
                    </div>
                </div>
            </div>
        </>
    );

    const youtube = (
        <>
            <div className="source-preview-instagram source-preview-container">
                <div className="preview-header">
                    <div className="social-icon youtube" />
                    <div className="preview-header-location">{locationNames.youtube}</div>
                </div>
                <div className="preview-content">
                    <ErrorDisplay publisherErrors={publisherErrors} component="MEDIA" source="YOUTUBE" />
                    <div className="preview-content-media">{buildCollage(collageData, 1, 1)}</div>
                    <ErrorDisplay publisherErrors={publisherErrors} component="TITLE" source="YOUTUBE" />
                    <div className="preview-title-display">{title}</div>
                    <ErrorDisplay publisherErrors={publisherErrors} component="CONTENT" source="YOUTUBE" />
                    <div className="preview-content-display">{getRenderedContentBySource('YOUTUBE')}</div>
                </div>
                <div className="preview-footer">
                    <div className="preview-footer-iconset">
                        <i className="i icon-thumbs-up" />
                        <span>0</span>
                    </div>
                    <div className="preview-footer-iconset">
                        <i className="i icon-thumbs-down" />
                        <span>0</span>
                    </div>
                </div>
            </div>
        </>
    );

    const igStories = (
        <>
            <div className="source-preview-instagram source-preview-container">
                <div className="preview-header">
                    <div className="social-icon ig_stories" />
                    <div className="preview-header-location">{locationNames.igStories}</div>
                </div>
                <div className="preview-content">
                    <ErrorDisplay publisherErrors={publisherErrors} component="MEDIA" source="IG_STORIES" />
                    <div className="preview-content-media">{buildCollage(collageData, 10, 5)}</div>
                    <ErrorDisplay publisherErrors={publisherErrors} component="CONTENT" source="IG_STORIES" />
                    <div className="preview-content-display">{getRenderedContentBySource('IG_STORIES')}</div>
                </div>
                <div className="preview-footer">
                    <div className="preview-footer-iconset">
                        <i className="i icon-heart" />
                        <span>{t('LIKE')}</span>
                    </div>
                    <div className="preview-footer-iconset">
                        <i className="i icon-comment-alt-dots" />
                        <span>{t('COMMENT')}</span>
                    </div>
                </div>
            </div>
        </>
    );

    const tiktok = (
        <>
            <div className="source-preview-tiktok source-preview-container">
                <div className="preview-header">
                    <div className="social-icon tiktok" />
                    <div className="preview-header-location">{locationNames.tiktok}</div>
                </div>
                <div className="preview-content">
                    <ErrorDisplay publisherErrors={publisherErrors} component="MEDIA" source="TIKTOK" />
                    <div className="preview-content-media">{buildCollage(collageData, 1, 1)}</div>
                    <ErrorDisplay publisherErrors={publisherErrors} component="CONTENT" source="TIKTOK" />
                    <div className="preview-content-display">{getRenderedContentBySource('TIKTOK')}</div>
                </div>
                <div className="preview-footer">
                    <div className="preview-footer-iconset">
                        <i className="i icon-heart" />
                        <span>{t('LIKE')}</span>
                    </div>
                    <div className="preview-footer-iconset">
                        <i className="i icon-comment-alt-dots" />
                        <span>{t('COMMENT')}</span>
                    </div>
                    <div className="preview-footer-iconset">
                        <i className="i icon-share-alt" />
                        <span>{t('SHARE')}</span>
                    </div>
                </div>
            </div>
        </>
    );

    const placeholder = (
        <div className="source-preview-placeholder">
            <h2>{t('START_COMPOSING_TO_PREVIEW_YOUR_POST')}</h2>
            <div id="placeholder-1" className="placeholder-container">
                <div className="placeholder-header">
                    <div className="placeholder-profile" />
                </div>
                <div className="placeholder-content">
                    <div className="placeholder-photo" />
                </div>
            </div>
            <div id="placeholder-2" className="placeholder-container">
                <div className="placeholder-header">
                    <div className="placeholder-profile" />
                    <div className="placeholder-text" />
                </div>
                <div className="placeholder-content">
                    <div className="placeholder-photo" />
                    <div className="placeholder-photo" />
                </div>
            </div>
            <div id="placeholder-3" className="placeholder-container">
                <div className="placeholder-header">
                    <div className="placeholder-profile" />
                </div>
                <div className="placeholder-content">
                    <div className="placeholder-text" />
                    <div className="placeholder-text" />
                    <div className="placeholder-text" />
                </div>
            </div>
        </div>
    );

    const shouldShowSource = (sourceName: SourceName): boolean => {
        if (customizeSourceKey === sourceName || customizeSourceKey === 'ALL_SOURCES') {
            return selectedSources[sourceName];
        }
        return false;
    };

    return (
        <div className="r4e-social-source-preview">
            {numSelectedSources === 0 && placeholder}
            {shouldShowSource('FACEBOOK') && facebook}
            {shouldShowSource('TWITTER') && twitter}
            {shouldShowSource('INSTAGRAM') && instagram}
            {shouldShowSource('IG_REELS') && igReels}
            {shouldShowSource('GOOGLE_PLACES') && gmb}
            {shouldShowSource('LINKEDIN') && linkedin}
            {shouldShowSource('YOUTUBE') && youtube}
            {shouldShowSource('TIKTOK') && tiktok}
            {/* non-API sources */}
            {shouldShowSource('IG_STORIES') && igStories}
        </div>
    );
};
